/**
 * The public form (`specs/M4-V8-forms.md` §6), rendered **outside the app shell** for a visitor
 * with no session — the same shape as the auth screens `[F4§7.1]`.
 *
 * Every number below is measured on monday's own public form
 * (`discovery/ui-reference/forms/sandbox-public-desktop.json`, `-mobile.json`):
 *   - page `rgb(246,247,251)`, a white card **640** wide with **48 px** inner padding, so the
 *     content column is **544**;
 *   - the title is 30/42 and a question label 18/24 Poppins 500;
 *   - the input is **544 × 40**, radius 4, `1px solid rgb(195,198,212)`, **28 px** under the
 *     label, and the pitch to the next label is **100 px**;
 *   - Submit is **84 × 38** on `rgb(0,115,234)` in a sticky footer;
 *   - at 390 the card is the viewport minus 8 px a side and the content column is **278**.
 *
 * The page boots from `<script id="__form">`, which the api inlines, so the first paint needs no
 * round trip; a client-side navigation falls back to `GET /forms/:token/data`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import {
  characterLimitFor,
  validateFormAnswers,
  visibleQuestions,
  type FormErrorCode,
  type FormQuestion,
} from '@elizmonday/shared';
import type { PublicForm } from './types';
import { QuestionField } from './QuestionField';
import './forms.css';

const ERROR_TEXT: Record<FormErrorCode, string> = {
  required: 'Required',
  too_long: 'Too long',
  not_a_number: 'Enter a number',
  invalid_email: 'Enter a valid email',
  invalid_url: 'Enter a valid link',
  invalid_date: 'Enter a valid date',
  invalid_option: 'Choose one of the options',
  invalid_rating: 'Enter a rating',
};

/** monday's own toast, verbatim. */
export const VALIDATION_TOAST = 'Please fill out all required fields';

function readInlineForm(): PublicForm | null {
  const node = document.getElementById('__form');
  if (node === null) return null;
  try {
    return JSON.parse(node.textContent ?? '') as PublicForm;
  } catch {
    return null;
  }
}

export function PublicFormPage({ initial }: { initial?: PublicForm | null } = {}) {
  const params = useParams();
  const token = params['token'] ?? initial?.token ?? '';
  const [form, setForm] = useState<PublicForm | null>(initial ?? readInlineForm());
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'sent'>(
    initial ?? readInlineForm() ? 'ready' : 'loading',
  );
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, FormErrorCode>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (form !== null || token === '') return;
    let cancelled = false;
    void (async () => {
      const response = await fetch(`/forms/${token}/data`, { credentials: 'omit' }).catch(() => null);
      if (cancelled) return;
      if (response === null || !response.ok) { setState('missing'); return; }
      setForm((await response.json()) as PublicForm);
      setState('ready');
    })();
    return () => { cancelled = true; };
  }, [form, token]);

  const shown = useMemo<FormQuestion[]>(
    () => (form === null ? [] : visibleQuestions(form.questions, form.settings, answers)),
    [form, answers],
  );

  const submit = useCallback(async () => {
    if (form === null || sending) return;
    const result = validateFormAnswers(shown, form.settings, answers);
    if (!result.ok) {
      setErrors(result.errors);
      setToast(VALIDATION_TOAST);
      return;
    }
    setErrors({});
    setSending(true);
    const response = await fetch(`/forms/${form.token}/submissions`, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ answers, renderId: form.renderId, __hp: '' }),
    }).catch(() => null);
    setSending(false);
    if (response === null) { setToast('Something went wrong. Try again.'); return; }
    if (response.status === 429) { setToast('Too many submissions. Try again later.'); return; }
    if (response.status === 422) {
      const body = (await response.json().catch(() => ({}))) as { errors?: Record<string, FormErrorCode> };
      setErrors(body.errors ?? {});
      setToast(VALIDATION_TOAST);
      return;
    }
    if (!response.ok) { setToast('This form is no longer accepting responses.'); return; }
    if (form.settings.redirect.enabled && form.settings.redirect.url !== null) {
      window.location.href = form.settings.redirect.url;
      return;
    }
    setState('sent');
  }, [answers, form, sending, shown]);

  if (state === 'loading') {
    return <div className="ez-form-page" data-testid="public-form-loading" />;
  }
  if (state === 'missing' || form === null) {
    return (
      <div className="ez-form-page">
        <main className="ez-form-card" data-testid="public-form-missing">
          <h1 className="ez-form-card__title">This form is not available</h1>
          <p className="ez-form-card__intro">
            The link may have expired, or the form may no longer be accepting responses.
          </p>
        </main>
      </div>
    );
  }

  if (state === 'sent') {
    return (
      <div className="ez-form-page">
        <main className="ez-form-card ez-form-card--thanks" data-testid="public-form-thankyou">
          <h1 className="ez-form-card__title">{form.settings.successTitle ?? 'Thank you!'}</h1>
          {form.settings.successContent !== null ? (
            <p className="ez-form-card__intro">{form.settings.successContent}</p>
          ) : null}
          {form.settings.submitAnother === 'click' ? (
            <button
              type="button"
              className="ez-form-submit"
              data-testid="public-form-submit-another"
              onClick={() => { setAnswers({}); setErrors({}); setState('ready'); }}
            >
              Submit another response
            </button>
          ) : null}
        </main>
      </div>
    );
  }

  const header = form.appearance.headerColor;
  return (
    <div className="ez-form-page" data-testid="public-form">
      {header !== null ? (
        <div className="ez-form-header" style={{ backgroundColor: header }} data-testid="public-form-header" />
      ) : null}
      {toast !== null ? (
        <div className="ez-form-toast" role="alert" data-testid="public-form-toast">
          {toast}
          <button type="button" aria-label="Dismiss" data-testid="public-form-toast-dismiss" onClick={() => setToast(null)}>×</button>
        </div>
      ) : null}

      <main className="ez-form-card">
        {form.appearance.logoUrl !== null ? (
          <img className="ez-form-card__logo" src={form.appearance.logoUrl} alt="" data-testid="public-form-logo" />
        ) : null}
        <h1 className="ez-form-card__title" data-testid="public-form-title">{form.title}</h1>
        {form.description !== null ? (
          <p className="ez-form-card__intro">{form.description}</p>
        ) : null}

        <form
          noValidate
          data-testid="public-form-form"
          onSubmit={(event) => { event.preventDefault(); void submit(); }}
        >
          {shown.map((question) => (
            <QuestionField
              key={question.id}
              question={question}
              data-testid={`question-${question.id}`}
              value={answers[question.id]}
              error={errors[question.id] === undefined ? null : ERROR_TEXT[errors[question.id]!]}
              limit={characterLimitFor(question)}
              onChange={(next) => setAnswers((current) => ({ ...current, [question.id]: next }))}
            />
          ))}

          {/* The honeypot: hidden from people, irresistible to a bot (§5.1). */}
          <input
            className="ez-form-hp"
            type="text"
            name="__hp"
            data-testid="public-form-honeypot"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            onChange={() => undefined}
          />
        </form>
      </main>

      <footer className="ez-form-footer">
        <button
          type="button"
          className="ez-form-submit"
          data-testid="public-form-submit"
          disabled={sending}
          onClick={() => void submit()}
        >
          Submit
        </button>
      </footer>

      {form.settings.hideBranding ? null : (
        <div className="ez-form-badge" data-testid="public-form-badge">Eli Z Monday forms</div>
      )}
    </div>
  );
}
